import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronRight,
  Check,
  Home,
  Camera,
  Share2,
  Briefcase,
  Plus,
  Loader2,
  Sparkles,
} from "lucide-react";
import Header from "../components/Header";
import LiveCoach from "../components/LiveCoach";
import RoutineDisclosure from "../components/RoutineDisclosure";
import RoutineGlowJournal from "../components/RoutineGlowJournal";
import RoutineStepsSidebar from "../components/RoutineStepsSidebar";
import RoutineActiveStep from "../components/RoutineActiveStep";
import MiniFeedbackToast from "../components/MiniFeedbackToast";
import TailorSignalsPanel from "../components/TailorSignalsPanel";
import PersonalizedRecommendations from "../components/PersonalizedRecommendations";
import { SHADE_MATCHING_UI_ENABLED } from "../lib/featureFlags";
import FaceShapeChip from "../components/FaceShapeChip";
import EyeShapeChip from "../components/EyeShapeChip";
import ShareCard from "../components/ShareCard";
import ShareJourneyCard from "../components/ShareJourneyCard";
import DemoScript from "../components/DemoScript";
import GuestSaveRoutineModal from "../components/GuestSaveRoutineModal";
import { track } from "../lib/analytics";
import { trackFP } from "../lib/firstPartyAnalytics";
import { useAuth } from "../context/AuthContext";
import { useSession } from "../context/SessionContext";
import { useStealth } from "../context/StealthContext";
import { fetchRoutine, fetchProducts, fetchSnapshots, deleteSnapshot, fetchArsenal, addArsenalItem, fetchTechnique } from "../lib/api";
import { useTTS } from "../hooks/useTTS";
import {
  stealthGetRoutine,
  stealthListSnapshots,
  stealthDeleteSnapshot,
} from "../lib/stealthDb";
import { API } from "../lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import DemoPrivacyNotice from "../components/DemoPrivacyNotice";

const Routine = ({ ephemeral = false }) => {
  const { t } = useTranslation();
  const { id: routeId } = useParams();
  const id = ephemeral ? "demo-session" : routeId;
  const navigate = useNavigate();
  const { routine: ctxRoutine, setRoutine, preferences, photo: sessionPhoto } = useSession();
  const { stealth } = useStealth();
  const { user } = useAuth();
  const [routine, setLocalRoutine] = useState(
    ephemeral ? ctxRoutine : ctxRoutine?.id === id ? ctxRoutine : null,
  );
  const [loading, setLoading] = useState(!routine);
  const [products, setProducts] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState(new Set());
  const [mirrorOpen, setMirrorOpen] = useState(false);
  const [shareLookOpen, setShareLookOpen] = useState(false);
  const [shareJourneyOpen, setShareJourneyOpen] = useState(false);
  // Lightweight TTS instance for the demo-script overlay (founder-only,
  // gated on demo mode + admin inside DemoScript). Not used by the
  // routine UI directly — the AR mirror has its own TTS instance.
  const demoTts = useTTS({ persist: !ephemeral, remote: !ephemeral });
  const [snapshots, setSnapshots] = useState([]);
  // Beauty Arsenal — products the user has logged as already-owned.
  // Hydrated on mount from /api/profile/arsenal. We use this to render
  // a "you already own" chip on each routine step whose category
  // matches an arsenal item — closing the loop between the Settings
  // logger and the routine experience.
  const [arsenal, setArsenal] = useState([]);
  // CV-backed per-category recommendations, when present on the routine.
  // `null` == absent (old saved routine, or low-confidence CV pass).
  // `{foundation: [...], color_corrector: [...], contour: [...]}` when available.
  const [cvRecommendations, setCvRecommendations] = useState(
    routine?.product_recommendations || null,
  );
  // Structured skin signals (mst_bucket, undertone, confidence_band, ...).
  // Drives the SkinSignalBadge + TailorSignalsPanel gating.
  const [skinSignals, setSkinSignals] = useState(
    routine?.skin_signals || null,
  );
  // Mini-feedback intercept state — counts snapshots taken DURING the
  // current mirror session so we know if the user closed without saving.
  const mirrorSessionSnaps = React.useRef(0);
  const [showMiniFeedback, setShowMiniFeedback] = useState(false);

  // Iter 84 (Feb 28 2026) — ref on the active step card so the
  // Next button can scroll it into view only when it's offscreen.
  // Replaces the previous `window.scrollTo({top:0})` which forced
  // a disorienting jump-to-top on every step advance, even when the
  // step card was already in view (mobile pain point flagged by the
  // founder). `block: "nearest"` is a no-op for already-visible
  // elements — exactly the founder's spec "keep the newly active
  // step in view only if needed."
  const stepCardRef = useRef(null);
  const miniFeedbackTimer = React.useRef(null);

  // Silent retention telemetry. We track whether the mirror was EVER
  // opened during this routine view, plus how long the user dwelled.
  // No user-facing UI — these events are admin-only signals so we know
  // the difference between bouncers, scanners, and lingerers.
  const mirrorEverOpened = React.useRef(false);
  const routineMountTime = React.useRef(Date.now());
  const lingerSignalFired = React.useRef(false);

  // Feb 2026 capture-first redesign: soft signup modal for guests.
  // Surfaces ~12s after the routine renders (Phase 2 retention
  // overhaul — was 5s, raised to give the user real time with the
  // result before the ask). Session-scoped: once dismissed, won't
  // re-appear in the same session, but the persistent "Keep my
  // routine" CTA at the bottom remains. Auth users never see it.
  const [showSaveModal, setShowSaveModal] = useState(false);
  const saveModalShownRef = React.useRef(false);
  useEffect(() => {
    if (ephemeral) return undefined;
    if (user) return undefined; // signed-in users skip the gate
    if (!routine) return undefined; // wait for routine to render
    if (saveModalShownRef.current) return undefined;
    // Session-scoped dedupe — if the guest already dismissed the
    // modal during this browser session, don't pop it again.
    try {
      if (window.sessionStorage.getItem("trubeaute_save_modal_dismissed") === "1") {
        return undefined;
      }
    } catch {
      /* sessionStorage unavailable in some privacy modes — fall through */
    }
    const timer = setTimeout(() => {
      saveModalShownRef.current = true;
      setShowSaveModal(true);
    }, 12000);
    return () => clearTimeout(timer);
  }, [user, routine, ephemeral]);

  const handleSaveModalDismiss = () => {
    try {
      window.sessionStorage.setItem("trubeaute_save_modal_dismissed", "1");
    } catch {
      /* ignore */
    }
    setShowSaveModal(false);
  };

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        if (ephemeral) {
          if (!routine) {
            navigate("/demo", { replace: true });
            return;
          }
          const p = await fetchProducts();
          if (!ok) return;
          setProducts(p);
          setSnapshots([]);
          return;
        }
        if (!routine) {
          // ── Technique tutorials → mock-routine adapter ──────────
          // The technique catalog lives at /api/techniques/{slug} as
          // static content, not in the DB. When the URL is
          // `/routine/technique:smoky-eye`, load from that endpoint
          // and shape the response into a RoutineResponse so every
          // downstream Routine.jsx feature (AR mirror, step sidebar,
          // glow journal) keeps working unchanged. The fix here is
          // for the previously-broken handoff from /techniques/:id
          // ("Start the technique" used to navigate to /mirror which
          // didn't exist — now it routes through /routine/ correctly).
          let r;
          if (typeof id === "string" && id.startsWith("technique:")) {
            const slug = id.slice("technique:".length);
            const tech = await fetchTechnique(slug);
            if (!ok) return;
            if (!tech) {
              toast.error("That technique doesn't exist.");
              navigate("/techniques");
              return;
            }
            r = {
              id, // keep the prefixed id so the URL stays canonical
              intro: tech.intro || `Let's do ${tech.title} together.`,
              skin_observations: "",
              goal: "everyday",
              experience: tech.difficulty || "beginner",
              beard_shadow: false,
              steps: (tech.steps || []).map((s, i) => ({
                order: i + 1,
                title: s.title,
                category: s.category || "foundation",
                what: s.what || "",
                where: s.where || "",
                why: s.tip || "",
                tip: s.tip || null,
              })),
              closing: `That's the look — you nailed it.`,
              created_at: new Date().toISOString(),
              skin_signals: null,
              product_recommendations: null,
              personalisation: null,
              // Flag so the rest of the UI can render technique-mode
              // chrome differently if/when we want to.
              _technique_meta: {
                slug,
                title: tech.title,
                vibe: tech.vibe,
                total_minutes: tech.total_minutes,
                hero_emoji: tech.hero_emoji,
              },
            };
          } else {
            r = stealth ? await stealthGetRoutine(id) : await fetchRoutine(id);
          }
          if (!ok) return;
          if (!r) {
            toast.error("That routine isn't on this device.");
            navigate("/my-routines");
            return;
          }
          setLocalRoutine(r);
          setRoutine(r);
          // Pick up CV signals + recs from the loaded routine, if present.
          if (r.product_recommendations) setCvRecommendations(r.product_recommendations);
          if (r.skin_signals) setSkinSignals(r.skin_signals);
        }
        const p = await fetchProducts();
        if (!ok) return;
        setProducts(p);
        // Technique routines have no DB row → skip snapshot fetch
        // (which would 404 against the routines collection).
        const isTechnique = typeof id === "string" && id.startsWith("technique:");
        const snaps = isTechnique
          ? []
          : stealth
            ? await stealthListSnapshots({ routine_id: id })
            : await fetchSnapshots(id, true);
        if (!ok) return;
        setSnapshots(snaps);
        track("routine_viewed", { stealth, technique: isTechnique });
        // Canonical Feb 2026 conversion-funnel event (Step 7 · ACTIVATION).
        // Fires every time a routine renders to the DOM. The funnel
        // dashboard dedupes by user_id, so over-firing is harmless and
        // a single user re-viewing the same routine still counts as one
        // activated user. The North Star Metric (Weekly Activated Users)
        // is the unique-user count of this event over a rolling 7d window.
        // Uses trackFP so Stealth users are still on the funnel.
        trackFP("first_personalized_result_shown", {
          stealth,
          technique: isTechnique,
          routine_id: id,
        });
        // Canonical Step 8: for guests, the routine page itself is the
        // signup-prompt surface (their next natural step is to claim
        // the routine into a permanent account). Fire `signup_prompt_viewed`
        // immediately after activation so the funnel between activation
        // and signup is measurable. For authenticated users this event
        // is a no-op (gated on the `auth: "guest"` property).
        if (!user) {
          trackFP("signup_prompt_viewed", {
            source: "routine_page",
            routine_id: id,
          });
        }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[routine] load failed:", e?.message || e);
        }
        toast.error("We couldn't load that routine.");
        navigate("/my-routines");
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
    // `user` intentionally omitted — re-fetching the routine on every
    // auth-context refresh would clobber any unsaved edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, routine, setRoutine, navigate, stealth, ephemeral]);

  const onSnapshotSaved = (snap) => {
    setSnapshots((prev) => [
      {
        id: snap.id,
        routine_id: snap.routine_id,
        step_idx: snap.step_idx,
        step_title: snap.step_title,
        mime_type: snap.mime_type,
        image_base64: snap.image_base64,
        created_at: snap.created_at,
      },
      ...prev,
    ]);
  };

  // ── Beauty Arsenal hydration ────────────────────────────────────
  // Authed users only; stealth mode keeps the arsenal server-side
  // off-limits so we don't surface a remote profile in local-only
  // mode. Failure is silent — empty arsenal renders no chips.
  useEffect(() => {
    if (ephemeral) return undefined;
    if (stealth) return;
    let alive = true;
    (async () => {
      try {
        const data = await fetchArsenal();
        if (alive) setArsenal(data?.items || []);
      } catch {
        if (alive) setArsenal([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [stealth, ephemeral]);

  // ── Silent retention telemetry (no user-facing UI) ──────────────────
  // Fires `routine_no_mirror_10min` if the user is STILL on the routine
  // page 10 minutes in without having opened the mirror once. Cancelled
  // the moment the mirror opens. Stealth-aware via `track()`.
  useEffect(() => {
    if (ephemeral) return undefined;
    const t = setTimeout(() => {
      if (!mirrorEverOpened.current && !lingerSignalFired.current) {
        lingerSignalFired.current = true;
        track("routine_no_mirror_10min");
      }
    }, 10 * 60 * 1000);
    return () => clearTimeout(t);
  }, [ephemeral]);

  // Fires `routine_no_mirror_left` on unmount (route change away from
  // /routine/:id) IF the mirror was never opened. Carries dwell_seconds
  // so we can distinguish bouncers (<30s), scanners (1-3min), and
  // lingerers (3min+) without exposing any of this to the user.
  useEffect(() => {
    if (ephemeral) return undefined;
    // Capture the ref's current value INSIDE the effect so the cleanup
    // closure uses the mount-time value rather than reading `.current`
    // at unmount (which React lint warns about — the ref could legally
    // change between effect-run and cleanup).
    const mountedAt = routineMountTime.current;
    return () => {
      if (!mirrorEverOpened.current) {
        const dwell = Math.round((Date.now() - mountedAt) / 1000);
        track("routine_no_mirror_left", { dwell_seconds: dwell });
      }
    };
  }, [ephemeral]);

  const onDeleteSnap = async (snapId) => {
    if (!window.confirm("Remove this glow moment?")) return;
    if (ephemeral) {
      setSnapshots((prev) => prev.filter((s) => s.id !== snapId));
      return;
    }
    try {
      await deleteSnapshot(snapId);
      setSnapshots((prev) => prev.filter((s) => s.id !== snapId));
      toast.success("Removed from your journal.");
    } catch {
      toast.error("Could not remove snapshot.");
    }
  };

  const currentStep = routine?.steps?.[activeStep];

  // CV-backed explain string for each product ID, if present in the
  // recommendation payload. Enables per-card "why this matches you"
  // copy inside the step's product grid.
  const cvExplainById = useMemo(() => {
    const out = {};
    if (!cvRecommendations) return out;
    Object.values(cvRecommendations).forEach((list) => {
      (list || []).forEach((rec) => {
        if (rec?.id) out[rec.id] = rec.explain;
      });
    });
    return out;
  }, [cvRecommendations]);

  // CV-backed top-3 shade matches for each product ID. Each entry is a
  // list of shade dicts from shades.Shade.to_dict():
  // {id, name, depth_label, undertone, mst_bucket, hex, corrector_color?}
  const cvShadesById = useMemo(() => {
    const out = {};
    if (!cvRecommendations) return out;
    Object.values(cvRecommendations).forEach((list) => {
      (list || []).forEach((rec) => {
        if (rec?.id && Array.isArray(rec.shades) && rec.shades.length > 0) {
          out[rec.id] = rec.shades;
        }
      });
    });
    return out;
  }, [cvRecommendations]);

  // Skin-concern / life-stage match reasons for each product ID. Only
  // present when the recommendation engine scored a meaningful fit
  // (matte for shine, dewy for dry patches, etc.). Rendered as a
  // secondary chip below the undertone-based explain chip.
  const cvSkinReasonById = useMemo(() => {
    const out = {};
    if (!cvRecommendations) return out;
    Object.values(cvRecommendations).forEach((list) => {
      (list || []).forEach((rec) => {
        if (rec?.id && rec.skin_match_reason) {
          out[rec.id] = rec.skin_match_reason;
        }
      });
    });
    return out;
  }, [cvRecommendations]);

  const stepProducts = useMemo(() => {
    if (!currentStep || !products.length) return [];
    const cvForCategory = cvRecommendations?.[currentStep.category];
    if (cvForCategory && cvForCategory.length > 0) {
      // Preserve CV ranking order — join against the full catalog to pick
      // up image_hint / description / url fields the engine doesn't repeat.
      const byId = new Map(products.map((p) => [p.id, p]));
      return cvForCategory
        .map((rec) => byId.get(rec.id))
        .filter(Boolean)
        .slice(0, 3);
    }
    return products
      .filter((p) => p.category === currentStep.category)
      .slice(0, 3);
  }, [currentStep, products, cvRecommendations]);

  // Arsenal items matching the current step's category. Rendered as
  // an affirming "you already own" chip above the product grid so
  // users see their own products before any new recommendation.
  const arsenalForStep = useMemo(() => {
    if (!currentStep || !arsenal.length) return [];
    return arsenal.filter((it) => it.category === currentStep.category);
  }, [currentStep, arsenal]);

  // Lookup map of "brand|name" (case-folded) → arsenal item, so each
  // product card can render either "+ Add to Arsenal" OR a steady
  // "✓ In your arsenal" badge depending on whether the user has
  // already logged this exact product.
  const arsenalByBrandName = useMemo(() => {
    const out = new Map();
    arsenal.forEach((it) => {
      const k = `${(it.brand || "").trim().toLowerCase()}|${(it.name || "").trim().toLowerCase()}`;
      out.set(k, it);
    });
    return out;
  }, [arsenal]);

  // ── End-of-routine recap candidates ─────────────────────────────
  // Every product recommended in this routine, across all categories,
  // that the user hasn't yet added to their Beauty Arsenal. Used to
  // power the soft "Did you grab any of these?" recap below the
  // closing line — a passive logging moment so users don't have to
  // remember each purchase later. Dedupes by brand+name so the same
  // product showing up in two category buckets only appears once.
  const unsavedRecapProducts = useMemo(() => {
    if (!cvRecommendations) return [];
    const seen = new Set();
    const out = [];
    Object.values(cvRecommendations).forEach((list) => {
      (list || []).forEach((rec) => {
        const key = `${(rec.brand || "").trim().toLowerCase()}|${(rec.name || "").trim().toLowerCase()}`;
        if (seen.has(key)) return;
        seen.add(key);
        if (!arsenalByBrandName.has(key)) {
          // Pull the rest of the product fields from the catalog by id
          // so the recap card has access to category + everything else.
          const fromCatalog = products.find((p) => p.id === rec.id);
          out.push(fromCatalog || rec);
        }
      });
    });
    // Hard cap at 6 to keep the recap card scannable; users with huge
    // routines can revisit the Settings arsenal page to add more.
    return out.slice(0, 6);
  }, [cvRecommendations, arsenalByBrandName, products]);

  // Per-product-card "adding to arsenal" state. Holds the product IDs
  // currently mid-POST so we can render a spinner + disable the button.
  // After success, the product moves into `arsenal` state and the
  // button toggles to the "In your arsenal" badge automatically.
  const [arsenalAdding, setArsenalAdding] = useState(() => new Set());
  // Skip-state for the end-of-routine arsenal recap card. Persisted
  // to localStorage per routine so dismissing it doesn't re-show on
  // a page reload. Storage key uses the routine ID so each routine
  // has its own opt-out state.
  const [recapDismissed, setRecapDismissed] = useState(() => {
    if (ephemeral) return true;
    try {
      return !!localStorage.getItem(`trubeaute_recap_dismissed_${id}`);
    } catch {
      return false;
    }
  });

  const handleAddProductToArsenal = async (product) => {
    if (ephemeral) return;
    if (!product) return;
    const key = `${product.brand.trim().toLowerCase()}|${product.name.trim().toLowerCase()}`;
    if (arsenalByBrandName.has(key)) return; // already logged
    setArsenalAdding((prev) => {
      const next = new Set(prev);
      next.add(product.id);
      return next;
    });
    try {
      const res = await addArsenalItem({
        brand: product.brand,
        name: product.name,
        category: product.category,
        // No shade by default — user can edit in Settings if they want.
        // We keep this one-tap action intentionally frictionless.
        shade: null,
        notes: null,
      });
      setArsenal(res.items || []);
      track("arsenal_item_added", {
        category: product.category,
        source: "routine_card",
        has_shade: false,
      });
      toast.success(`${product.brand} ${product.name} saved to your arsenal.`);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Couldn't save to your arsenal.",
      );
    } finally {
      setArsenalAdding((prev) => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });
    }
  };

  const toggleComplete = (idx) => {
    const n = new Set(completed);
    if (n.has(idx)) n.delete(idx);
    else n.add(idx);
    setCompleted(n);
  };

  const next = () => {
    if (!routine) return;
    if (activeStep < routine.steps.length - 1) {
      setCompleted(new Set([...completed, activeStep]));
      setActiveStep(activeStep + 1);
      // Scroll only if the step card is offscreen. `block: "nearest"`
      // is a no-op when the element is already visible — preserves
      // the user's scroll context instead of yanking them to the top.
      // requestAnimationFrame waits for the next render so the new
      // card layout exists before we measure.
      requestAnimationFrame(() => {
        stepCardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      });
    } else {
      setCompleted(new Set([...completed, activeStep]));
      toast.success("You did it — and you look absolutely radiant.");
    }
  };

  const prev = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  const saveEphemeralSnapshot = useCallback(async (payload) => ({
    ...payload,
    id: `demo-snapshot-${Date.now()}`,
    created_at: new Date().toISOString(),
  }), []);

  if (loading) {
    return (
      <div className="min-h-screen trubeaute-gradient flex items-center justify-center">
        <div className="text-[#8A7D82]">Loading your routine…</div>
      </div>
    );
  }

  if (!routine) return null;

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <Header />

      {ephemeral && (
        <div className="pt-20 px-4 sm:px-6">
          <DemoPrivacyNotice />
        </div>
      )}

      <section className={`${ephemeral ? "pt-8" : "pt-24 sm:pt-32"} pb-20 px-4 sm:px-6`} data-testid="routine-root">
        <div className="max-w-5xl mx-auto">
          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center max-w-2xl mx-auto"
          >
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-3">
              {t("routine.kicker")}
            </span>
            <h1 className="font-serif italic text-2xl sm:text-4xl lg:text-5xl text-[#2C2123] leading-tight mb-4">
              {routine.intro}
            </h1>
            {routine.skin_observations && (
              <p className="text-[14px] sm:text-base text-[#5C4B51] leading-relaxed">
                {routine.skin_observations}
              </p>
            )}
            <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
              <span className="text-xs bg-[#F2E8E9] text-[#5D4050] rounded-full px-3 py-1">
                {t(`goalLabels.${routine.goal}`, { defaultValue: routine.goal })}
              </span>
              <span className="text-xs bg-[#F5EFEB] text-[#5C4B51] rounded-full px-3 py-1">
                {t(`expLabels.${routine.experience}`, { defaultValue: routine.experience })}
              </span>
              {/* Iter 85 — estimated time chip. ~2 min per step is a
                  conservative average across the corpus; gives users
                  an "I can fit this in" anchor without us having to
                  store an exact estimate on the routine row. */}
              <span
                className="text-xs bg-[#FAF6F2] text-[#5C4B51] rounded-full px-3 py-1"
                data-testid="routine-eta-chip"
              >
                {t("routine.totalTime", { minutes: Math.max(5, routine.steps.length * 2) })}
              </span>
              {routine.beard_shadow && (
                <span className="text-xs bg-[#F8EEEE] text-[#5D4050] rounded-full px-3 py-1">
                  {t("routine.includesColorCorrection")}
                </span>
              )}
            </div>
          </motion.div>

          {/* Progress Bar */}
          <div className="bg-white border border-[#EADDD7] rounded-2xl p-4 mb-6 soft-shadow">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-[#8A7D82] mb-3">
              <span>{t("routine.yourProgress")}</span>
              <span data-testid="routine-progress-text">
                {completed.size} / {routine.steps.length}
              </span>
            </div>
            <div className="h-2 w-full bg-[#F5EFEB] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#D5A8A4] to-[#9B6A6C]"
                initial={{ width: 0 }}
                animate={{
                  width: `${(completed.size / routine.steps.length) * 100}%`,
                }}
                transition={{ duration: 0.6 }}
              />
            </div>
            {completed.size === routine.steps.length && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="mt-4 pt-4 border-t border-[#F2E8E9] flex items-center justify-between gap-3 flex-wrap"
                data-testid="routine-complete-celebration"
              >
                <div className="text-sm text-[#5D4050]">
                  <span className="font-medium text-[#2C2123]">
                    {t("routine.youDidItHeader")}
                  </span>{" "}
                  {t("routine.everyStepDone")}
                </div>
                <button
                  type="button"
                  onClick={() => setShareLookOpen(true)}
                  className="inline-flex items-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-full px-4 py-2 text-sm font-medium transition-colors"
                  data-testid="routine-share-look-btn"
                >
                  <Share2 className="h-3.5 w-3.5" />
                  {t("routine.shareTonightsLook")}
                </button>
              </motion.div>
            )}
          </div>

          {/* Why this routine? — transparency + safety disclosure */}
          <div className="mb-6">
            <RoutineDisclosure routine={routine} preferences={preferences} />
          </div>

          {/* Face-shape chip — affirming callout if the AR mirror
              has already detected & saved the user's face shape. */}
          {!ephemeral && <FaceShapeChip />}

          {/* Eye-shape chip — same pattern for eye shape (hooded /
              monolid / etc). Surfaces the user's onboarding pick OR
              GPT-4o Vision's suggestion from the last selfie, with an
              inline 6-option override picker. Renders nothing when
              the user hasn't set/been-detected an eye shape yet. */}
          {!ephemeral && <EyeShapeChip />}

          {/* Help-us-tailor panel — only when CV confidence was low/unknown.
              Clears itself once the user submits and the recs refresh. */}
          {!ephemeral && skinSignals &&
            ["low", "unknown"].includes(skinSignals.confidence_band) && (
              <TailorSignalsPanel
                beardShadow={
                  skinSignals.beard_shadow_detected || routine.beard_shadow
                }
                onRecommendationsReady={(recs, signals) => {
                  if (recs) setCvRecommendations(recs);
                  if (signals) setSkinSignals(signals);
                }}
              />
            )}

          {/* Personalized Product Recommendations (Feb 27 2026) —
              replaces the earlier standalone ShadeMatchesCard. The
              same /api/shade-match/analyze payload now feeds a
              Trouvé-voice, product-first experience: foundation
              hero → concealer strategy (as steps) → lip tabs →
              save/retake/try-another CTA row. Backend engine
              untouched. Hidden until CV signals are ready.
              Temporarily gated off via SHADE_MATCHING_UI_ENABLED. */}
          {!ephemeral && SHADE_MATCHING_UI_ENABLED && skinSignals?.mst_bucket && (
            <div className="max-w-3xl mx-auto">
              <PersonalizedRecommendations
                skinSignals={skinSignals}
                preferences={{
                  beard_shadow: skinSignals.beard_shadow_detected || routine.beard_shadow,
                  redness: !!routine.has_redness,
                  hyperpigmentation: !!routine.has_hyperpigmentation,
                  preferred_finish: routine.preferred_finish,
                  coverage: routine.coverage,
                }}
                onRetake={() => navigate("/capture")}
                onTryAnother={() => navigate("/onboarding?refine=1")}
                onSaveRoutine={user ? null : () => setShowSaveModal(true)}
                onSaveProduct={user ? null : () => setShowSaveModal(true)}
              />
            </div>
          )}

          {/* Mirror entry lives inside RoutineActiveStep's new
              "Meet me at the mirror" card (iter 77) — single CTA,
              clear mode selection, no competing footer banner. */}

          <div className="grid lg:grid-cols-[280px_1fr] gap-6">
            {/* Steps list */}
            <RoutineStepsSidebar
              steps={routine.steps}
              activeStep={activeStep}
              completed={completed}
              onSelect={setActiveStep}
            />

            {/* Active step content — extracted to RoutineActiveStep
                so this file stays readable. Parent owns ALL state
                (activeStep, completed, arsenal mutation set); the
                child is pure UI + event delegation. */}
            <RoutineActiveStep
              routine={routine}
              activeStep={activeStep}
              currentStep={currentStep}
              completed={completed}
              onToggleComplete={toggleComplete}
              stepProducts={stepProducts}
              arsenalForStep={arsenalForStep}
              arsenalByBrandName={arsenalByBrandName}
              arsenalAdding={arsenalAdding}
              onAddProductToArsenal={handleAddProductToArsenal}
              cvExplainById={cvExplainById}
              cvSkinReasonById={cvSkinReasonById}
              cvShadesById={cvShadesById}
              skinSignals={skinSignals}
              cvRecommendations={cvRecommendations}
              onPrev={prev}
              onNext={next}
              onOpenMirror={(mode) => {
                // LiveCoach currently opens directly in guided mode. A stale
                // setMirrorMode() call used to throw here because that setter
                // does not exist, making the production CTA look completely
                // dead. Keep the mode on the analytics event, then open the
                // mirror without touching nonexistent state.
                track("learn_with_trouve_started", { at_step: activeStep, mode });
                mirrorEverOpened.current = true;
                mirrorSessionSnaps.current = 0;
                setMirrorOpen(true);
              }}
              user={user}
              sessionPhoto={sessionPhoto}
              cardRef={stepCardRef}
              ephemeral={ephemeral}
            />
          </div>

          {/* Closing — Trouvé's sign-off renders on its own line so
              the persona moment lands. We split on the last newline
              and style the "xo, Trouvé" tail in a smaller, more
              intimate weight; falls back gracefully if the LLM doesn't
              include the newline (older routines + edge cases). */}
          <div className="mt-10 text-center max-w-2xl mx-auto">
            {(() => {
              const closing = routine.closing || "";
              const idx = closing.lastIndexOf("\n");
              const hasSignoff = idx > 0;
              const body = hasSignoff ? closing.slice(0, idx) : closing;
              const signoff = hasSignoff ? closing.slice(idx + 1) : null;
              return (
                <>
                  <p
                    className="font-serif italic text-xl text-[#5D4050] leading-relaxed"
                    data-testid="routine-closing-body"
                  >
                    {body}
                  </p>
                  {signoff && (
                    <p
                      className="mt-3 font-serif italic text-[#9B6A6C] text-sm tracking-wide"
                      data-testid="routine-closing-signoff"
                    >
                      {signoff}
                    </p>
                  )}
                </>
              );
            })()}
          </div>

          {/* End-of-routine arsenal recap — passive logging prompt.
              Only renders when the user has unsaved products AND
              hasn't dismissed it for this routine. Tap any product
              to save in one click; arsenal chip + matching recs in
              future routines update immediately. Honors stealth mode
              by checking `arsenalByBrandName` (which is empty in
              stealth, making `unsavedRecapProducts` empty too). */}
          {!ephemeral && !recapDismissed && unsavedRecapProducts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-10 max-w-2xl mx-auto rounded-3xl bg-gradient-to-br from-[#F5DCD7]/40 to-[#FAF5F1] border border-[#EADDD7] p-6 sm:p-7 soft-shadow"
              data-testid="routine-arsenal-recap"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-white border border-[#EADDD7] flex items-center justify-center shrink-0">
                  <Briefcase className="h-5 w-5 text-[#9B6A6C]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base sm:text-lg font-medium text-[#2C2123] leading-tight">
                    {t("routine.recapHeading")}
                  </h4>
                  <p className="text-sm text-[#5C4B51] mt-1 leading-relaxed">
                    {t("routine.recapBody")}
                  </p>
                </div>
              </div>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                data-testid="routine-arsenal-recap-items"
              >
                {unsavedRecapProducts.map((p) => {
                  const key = `${(p.brand || "").trim().toLowerCase()}|${(p.name || "").trim().toLowerCase()}`;
                  const inArsenal = arsenalByBrandName.has(key);
                  const adding = arsenalAdding.has(p.id);
                  if (inArsenal) {
                    return (
                      <div
                        key={p.id}
                        className="flex items-center gap-2 p-3 rounded-xl bg-white/60 border border-[#EADDD7] text-sm"
                        data-testid={`routine-arsenal-recap-saved-${p.id}`}
                      >
                        <Check className="h-4 w-4 text-[#9B6A6C] shrink-0" />
                        <span className="text-[#5D4050] truncate">
                          <span className="font-medium">{p.brand}</span>{" "}
                          {p.name}
                        </span>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleAddProductToArsenal(p)}
                      disabled={adding}
                      className="group flex items-center gap-2 p-3 rounded-xl bg-white hover:bg-[#FFF6F2] border border-[#EADDD7] hover:border-[#D5A8A4] text-left text-sm transition-all disabled:opacity-60"
                      data-testid={`routine-arsenal-recap-add-${p.id}`}
                      aria-label={`Save ${p.brand} ${p.name} to my arsenal`}
                    >
                      {adding ? (
                        <Loader2 className="h-4 w-4 animate-spin text-[#9B6A6C] shrink-0" />
                      ) : (
                        <Plus className="h-4 w-4 text-[#9B6A6C] shrink-0 group-hover:scale-110 transition-transform" />
                      )}
                      <span className="text-[#2C2123] truncate min-w-0">
                        <span className="font-medium">{p.brand}</span>{" "}
                        <span className="text-[#5C4B51]">{p.name}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => {
                  try {
                    localStorage.setItem(`trubeaute_recap_dismissed_${id}`, "1");
                  } catch {
                    /* private mode → just dismiss in memory */
                  }
                  setRecapDismissed(true);
                  track("arsenal_recap_dismissed", {
                    unsaved_count: unsavedRecapProducts.length,
                  });
                }}
                className="block mx-auto mt-4 text-xs text-[#8A7D82] hover:text-[#5D4050] underline underline-offset-2 transition-colors"
                data-testid="routine-arsenal-recap-skip"
              >
                {t("routine.skipThisTime")}
              </button>
            </motion.div>
          )}

          {/* Glow journal — saved moments */}
          <RoutineGlowJournal snapshots={snapshots} onDelete={onDeleteSnap} />

          {/* Footer actions */}
          <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
            {!ephemeral && <button
              onClick={() => setShareLookOpen(true)}
              className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#7d5455] rounded-full px-7 py-3 transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
              data-testid="routine-share-look-btn"
            >
              <Share2 className="h-4 w-4" />
              {t("routine.shareMyLook")}
            </button>}
            {!ephemeral && <button
              onClick={() => {
                trackFP("share_card_journey_cta_clicked", {
                  routine_id: id,
                });
                setShareJourneyOpen(true);
              }}
              className="inline-flex items-center gap-2 border border-[#9B6A6C] text-[#9B6A6C] hover:bg-[#F8EEEE] rounded-full px-7 py-3 transition-all"
              data-testid="routine-share-journey-btn"
            >
              <Sparkles className="h-4 w-4" />
              {t("routine.shareMyJourney")}
            </button>}
            {!ephemeral && <button
              onClick={() => {
                // Canonical engagement signal — fires every time the
                // user opens the progressive-profiling loop, regardless
                // of whether they complete or abandon it. Stealth-aware
                // via trackFP so we don't lose Stealth users on the
                // engagement funnel.
                trackFP("refine_routine_clicked", {
                  routine_id: id,
                  technique: typeof id === "string" && id.startsWith("technique:"),
                });
                // Carry the routine id on the query string so the
                // refine flow can return the user to THIS routine
                // after they submit, independent of SessionContext.
                navigate(`/onboarding?refine=1&from=${encodeURIComponent(id)}`);
              }}
              className="inline-flex items-center gap-2 border border-[#D5A8A4] text-[#5D4050] hover:bg-[#F8EEEE] rounded-full px-6 py-3 transition-all"
              data-testid="routine-refine-btn"
            >
              <Sparkles className="h-4 w-4" />
              {t("routine.refineCta")}
            </button>}
            <button
              onClick={() => navigate(ephemeral ? "/demo" : "/")}
              className="inline-flex items-center gap-2 border border-[#D5A8A4] text-[#5D4050] hover:bg-[#F8EEEE] rounded-full px-6 py-3 transition-all"
              data-testid="routine-home-btn"
            >
              <Home className="h-4 w-4" /> {ephemeral ? t("demo.ephemeral.restart") : t("routine.backToHome")}
            </button>
            {!ephemeral && <button
              onClick={() => navigate("/my-routines")}
              className="inline-flex items-center gap-2 text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-4 py-2"
              data-testid="routine-view-all-btn"
            >
              {t("routine.viewAllSaved")}
            </button>}
          </div>
          {/* Persistent guest-only "Keep my routine" CTA — anyone who
              dismissed the 5-second modal still gets a soft repeat
              ask, framed around ownership. Auth users never see this
              row. */}
          {!ephemeral && !user && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => {
                  trackFP("signup_modal_save_clicked", {
                    source: "routine_footer_repeat",
                    routine_id: id,
                  });
                  const next = id ? `/routine/${id}` : "/my-routines";
                  navigate(
                    `/login?next=${encodeURIComponent(next)}&intent=save_routine`,
                  );
                }}
                className="inline-flex items-center gap-2 bg-[#2C2123] text-white hover:bg-[#3a2b2e] rounded-full px-6 py-3 text-sm font-medium transition-all shadow-[0_4px_14px_rgba(44,33,35,0.25)] hover:-translate-y-0.5"
                data-testid="routine-keep-routine-btn"
              >
                <Sparkles className="h-4 w-4" />
                {t("routine.keepRoutineCta")}
              </button>
            </div>
          )}
          {!ephemeral && <p
            className="mt-2 text-center text-xs text-[#8A7D82] max-w-md mx-auto"
            data-testid="routine-refine-helper"
          >
            {t("routine.refineHelper")}
          </p>}
        </div>
      </section>

      {/* AR Mirror — your drag mom in the mirror */}
      <AnimatePresence>
        {mirrorOpen && (
          <LiveCoach
            steps={routine.steps}
            initialStepIdx={activeStep}
            routineId={id}
            closing={routine.closing}
            onClose={() => {
              track("learn_with_trouve_closed_early", { at_step: activeStep });
              setMirrorOpen(false);
              // If the user closed without saving any snapshots in this
              // session, gently ask why after a 3-second beat. The beat
              // matters: ask too early and it feels intrusive; too late
              // and they've already moved on.
              const tookSnap = mirrorSessionSnaps.current > 0;
              if (!ephemeral && !tookSnap) {
                if (miniFeedbackTimer.current) clearTimeout(miniFeedbackTimer.current);
                miniFeedbackTimer.current = setTimeout(
                  () => setShowMiniFeedback(true),
                  3000,
                );
              }
            }}
            onStepDone={(idx) => setCompleted((prev) => new Set([...prev, idx]))}
            onSnapshotSaved={(snap) => {
              mirrorSessionSnaps.current += 1;
              setSnapshots((prev) => [snap, ...prev]);
            }}
            ephemeral={ephemeral}
            saveSnapshotOverride={ephemeral ? saveEphemeralSnapshot : undefined}
            snapshotSavedMessage={ephemeral ? t("demo.ephemeral.snapshotKept") : undefined}
          />
        )}
      </AnimatePresence>

      {/* Mini-feedback intercept — appears 3s after closing the mirror
          early without saving a snapshot. Catches the "almost finished"
          insights the analytics dashboard alone can't see. */}
      <AnimatePresence>
        {!ephemeral && showMiniFeedback && (
          <MiniFeedbackToast
            context="learn_with_trouve_closed_early"
            onClose={() => setShowMiniFeedback(false)}
          />
        )}
      </AnimatePresence>

      <ShareCard
        open={shareLookOpen}
        onClose={() => setShareLookOpen(false)}
        analyticsPrefix="look_share_card"
        snapshots={snapshots || []}
        payload={{
          lookName: (routine?.intro || "Tonight's look").replace(/[.!?]+$/, ""),
          subtitle: routine?.skin_observations || "",
        }}
      />

      {!ephemeral && <ShareJourneyCard
        open={shareJourneyOpen}
        onClose={() => setShareJourneyOpen(false)}
        routineId={id}
        routineGoal={routine?.goal}
      />}

      {/* Feb 2026 capture-first: soft signup modal for guests, fired
          ~5s after the routine renders. Auth users never see this —
          they already have an account to save into. */}
      {!ephemeral && !user && (
        <GuestSaveRoutineModal
          open={showSaveModal}
          onDismiss={handleSaveModalDismiss}
          routineId={id}
        />
      )}

      {/* Founder-only scripted demo tour. Renders nothing unless demo
          mode is active AND the user is admin AND they pressed the
          "Start scripted demo" button on the Settings card. */}
      {!ephemeral && <DemoScript tts={demoTts} />}
    </div>
  );
};

export default Routine;
